import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
   CRM_PACKAGES,
} from './crmPackagesMockData';
import './Crm.css';

const fmtAED = (n) => `AED ${Number(n || 0).toLocaleString('en-US', { maximumFractionDigits: 2 })}`;
const fmtDate = (value) => {
   if (!value) return '—';
   const d = new Date(value);
   if (Number.isNaN(d.getTime())) return '—';
   return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};
const daysUntil = (value) => {
   const d = new Date(value);
   if (Number.isNaN(d.getTime())) return null;
   const today = new Date();
   today.setHours(0, 0, 0, 0);
   d.setHours(0, 0, 0, 0);
   return Math.round((d.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
};
const isWithinPackageRange = (value, range) => {
   if (range === 'All time') return true;
   const date = new Date(value);
   if (Number.isNaN(date.getTime())) return false;
   const now = new Date();
   const start = new Date(now);
   start.setHours(0, 0, 0, 0);
   if (range === 'This Week') start.setDate(start.getDate() - 6);
   if (range === 'This Month') start.setDate(start.getDate() - 29);
   return date >= start && date <= now;
};

const statusPillStyle = (status) => {
   switch (status) {
      case 'Active': return { bg: '#e0f2ea', color: '#1a6641' };
      case 'Expiring Soon': return { bg: '#fef3c7', color: '#92400E' };
      case 'Expired': return { bg: '#f3f4f6', color: '#374151' };
      case 'Cancelled': return { bg: '#fee2e2', color: '#991b1b' };
      default: return { bg: '#f3f4f6', color: '#374151' };
   }
};

function StatusPill({ status }) {
   const cfg = statusPillStyle(status);
   return (
      <span className="ws-pill" style={{ background: cfg.bg, color: cfg.color }}>{status}</span>
   );
}

function UsageBar({ used, total }) {
   const pct = total ? Math.min(100, Math.round((used / total) * 100)) : 0;
   const color = pct >= 100 ? '#1a6641' : pct >= 60 ? '#4a3db8' : '#92400E';
   return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 90 }}>
         <div style={{ fontSize: 10, fontWeight: 700, color: '#111' }}>{used} / {total} washes</div>
         <div style={{ width: '100%', height: 5, background: '#eee', borderRadius: 3, overflow: 'hidden' }}>
            <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 3 }} />
         </div>
      </div>
   );
}

function DrawerField({ label, value, color }) {
   return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
         <div style={{ fontSize: 9, fontWeight: 700, color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</div>
         <div style={{ fontSize: 12, color: color || '#111', fontWeight: 600 }}>{value}</div>
      </div>
   );
}

const deliveryPillStyle = (status) => {
   switch (status) {
      case 'Completed': return { bg: '#d1fae5', color: '#065f46' };
      case 'Missed': return { bg: '#fee2e2', color: '#991b1b' };
      case 'Due today': return { bg: '#fef3c7', color: '#92400E' };
      case 'Cancelled': return { bg: '#f3f4f6', color: '#6b7280' };
      default: return { bg: '#eef2ff', color: '#4a3db8' }; // Scheduled
   }
};

function PackageDrawer({ pkg, onClose }) {
   if (!pkg) return null;
   const daysLeft = daysUntil(pkg.expiresOn);
   return (
      <div className="ws-package-drawer">
         <div className="ws-package-drawer-head">
            <div>
               <div style={{ fontSize: 12, fontWeight: 700, color: '#111' }}>Package Detail</div>
               <div style={{ fontSize: 10, color: '#6B5FE4', fontFamily: 'monospace', marginTop: 2 }}>{pkg.id}</div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
               <StatusPill status={pkg.status} />
               <button onClick={onClose} style={{ fontSize: 16, background: 'none', border: 'none', cursor: 'pointer', color: '#888', lineHeight: 1, padding: '0 4px' }}>✕</button>
            </div>
         </div>

         <div style={{ padding: 14, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <div style={{ gridColumn: '1 / -1' }}>
               <DrawerField label="Customer" value={`${pkg.customer} · ${pkg.email}`} />
            </div>
            <DrawerField label="Vehicle" value={<span>{pkg.vehicle} · <span className="ws-plate">{pkg.plate}</span></span>} />
            <DrawerField label="Car Type" value={pkg.carType} />
            <DrawerField label="Location" value={pkg.location} />
            <DrawerField label="Provider" value={pkg.provider} />
            <DrawerField label="Plan" value={<span style={{ color: '#4a3db8' }}>{pkg.plan}</span>} />
            <DrawerField label="Frequency" value={pkg.frequency} />
            <DrawerField label="Purchased" value={fmtDate(pkg.purchasedOn)} />
            <DrawerField
               label="Expires"
               value={pkg.status === 'Expired' ? fmtDate(pkg.expiresOn) : `${fmtDate(pkg.expiresOn)}${daysLeft != null && daysLeft >= 0 ? ` · ${daysLeft}d left` : ''}`}
               color={pkg.status === 'Expiring Soon' ? '#92400E' : undefined}
            />
            <div style={{ gridColumn: '1 / -1' }}>
               <DrawerField label="Wash Usage" value={<UsageBar used={pkg.washesUsed} total={pkg.planTier} />} />
            </div>
            <DrawerField label="Auto-Renew" value={pkg.autoRenew ? <span style={{ color: '#1a6641' }}>On</span> : <span style={{ color: '#991b1b' }}>Off</span>} />
            {pkg.status === 'Expired' && (
               <DrawerField label="Renewed" value={pkg.renewed ? <span style={{ color: '#1a6641' }}>Yes</span> : <span style={{ color: '#991b1b' }}>No — churned</span>} />
            )}

            <div style={{ gridColumn: '1 / -1' }}>
               <div style={{ fontSize: 9, fontWeight: 700, color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>
                  Financial Breakdown
               </div>
               <div style={{ border: '1px solid #eee', borderRadius: 10, overflow: 'hidden' }}>
                  {[
                     { label: 'Package Price', value: fmtAED(pkg.money.packagePrice), bg: '#fff' },
                     { label: 'Platform Fee', value: fmtAED(pkg.money.platformFee), bg: '#f7f5ff', color: '#4a3db8' },
                     { label: 'Charged to Customer', value: fmtAED(pkg.money.customerCharged), bg: '#f9f9f9', bold: true },
                     { label: 'Stripe Processing Fee', value: `−${fmtAED(pkg.money.stripeFee)}`, bg: '#fff', color: '#991b1b' },
                     { label: 'Washta Commission (8%)', value: `+${fmtAED(pkg.money.commission)}`, bg: '#f7f5ff', color: '#4a3db8' },
                     { label: 'Washta Net Revenue', value: fmtAED(pkg.money.washtaNet), bg: '#f0fdf4', color: '#065f46', bold: true },
                     { label: 'Provider Payout', value: fmtAED(pkg.money.providerPayout), bg: '#fff' },
                  ].map((row, i, arr) => (
                     <div key={row.label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', background: row.bg, borderBottom: i < arr.length - 1 ? '1px solid #f0f0f0' : 'none' }}>
                        <span style={{ fontSize: 10, color: '#555' }}>{row.label}</span>
                        <span style={{ fontSize: 11, fontFamily: 'monospace', fontWeight: row.bold ? 800 : 600, color: row.color || '#111' }}>{row.value}</span>
                     </div>
                  ))}
               </div>
            </div>

            <div style={{ gridColumn: '1 / -1' }}>
               <div style={{ fontSize: 9, fontWeight: 700, color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>
                  Delivery History
               </div>
               <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                  {pkg.deliveries.map((d, i) => {
                     const cfg = deliveryPillStyle(d.status);
                     return (
                        <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 10 }}>
                           <span style={{ color: '#555' }}>{d.date}</span>
                           <span style={{ color: '#aaa', flex: 1, textAlign: 'right', marginRight: 8, fontSize: 9 }}>{d.note}</span>
                           <span style={{ padding: '2px 7px', borderRadius: 100, fontWeight: 700, background: cfg.bg, color: cfg.color, whiteSpace: 'nowrap' }}>{d.status}</span>
                        </div>
                     );
                  })}
               </div>
            </div>
         </div>
      </div>
   );
}

const rankClass = (i) => (i === 0 ? 'ws-rd1' : i === 1 ? 'ws-rd2' : 'ws-rd3');

const emptyMoney = () => ({ count: 0, packagePrice: 0, platformFee: 0, customerCharged: 0, stripeFee: 0, commission: 0, washtaNet: 0, providerPayout: 0 });
const addMoney = (acc, p) => {
   acc.count += 1;
   acc.packagePrice += p.money.packagePrice;
   acc.platformFee += p.money.platformFee;
   acc.customerCharged += p.money.customerCharged;
   acc.stripeFee += p.money.stripeFee;
   acc.commission += p.money.commission;
   acc.washtaNet += p.money.washtaNet;
   acc.providerPayout += p.money.providerPayout;
   return acc;
};

const CrmPackages = () => {
   const [packages, setPackages] = useState(CRM_PACKAGES);
   const [usingMockData, setUsingMockData] = useState(true);
   const [statusFilter, setStatusFilter] = useState('All Statuses');
   const [planFilter, setPlanFilter] = useState('All Plans');
   const [locationFilter, setLocationFilter] = useState('All Locations');
   const [providerFilter, setProviderFilter] = useState('All Providers');
   const [search, setSearch] = useState('');
   const [range, setRange] = useState('Today');
   const [selectedPkg, setSelectedPkg] = useState(null);
   const [locationsOpen, setLocationsOpen] = useState(true);
   const [planOpen, setPlanOpen] = useState(true);

   useEffect(() => {
      let mounted = true;
      const loadPackages = async () => {
         const token = localStorage.getItem('accessTokenAdmin');
         if (!token) return;
         try {
            const baseUrl = process.env.REACT_APP_API_URL || 'https://production.washta.com';
            const response = await axios.get(`${baseUrl}/api/admin/v2/dashboard/crm-packages`, {
               headers: { Authorization: `Bearer ${token}` },
            });
            const data = response?.data?.data;
            if (mounted && response?.data?.status && Array.isArray(data?.packages)) {
               setPackages(data.packages);
               setUsingMockData(false);
            }
         } catch (error) {
            console.error('Failed to load CRM package data; showing mock data:', error);
         }
      };
      loadPackages();
      return () => { mounted = false; };
   }, []);

   const packageLocations = useMemo(() => Array.from(new Set(packages.map((p) => p.location).filter(Boolean))).sort(), [packages]);
   const packageProviders = useMemo(() => Array.from(new Set(packages.map((p) => p.provider).filter(Boolean))).sort(), [packages]);
   const packagePlans = useMemo(() => Array.from(new Set(packages.map((p) => p.plan).filter(Boolean))).sort(), [packages]);
   const packageStatuses = useMemo(() => Array.from(new Set([...['Active', 'Expiring Soon', 'Expired', 'Cancelled'], ...packages.map((p) => p.status).filter(Boolean)])), [packages]);

   const rangePackages = useMemo(() => packages.filter((p) => isWithinPackageRange(p.purchasedOn, range)), [packages, range]);

   const filtered = useMemo(() => {
      const q = search.trim().toLowerCase();
      return rangePackages.filter((p) => {
         if (statusFilter !== 'All Statuses' && p.status !== statusFilter) return false;
         if (planFilter !== 'All Plans' && p.plan !== planFilter) return false;
         if (locationFilter !== 'All Locations' && p.location !== locationFilter) return false;
         if (providerFilter !== 'All Providers' && p.provider !== providerFilter) return false;
         if (q && !`${p.customer} ${p.email} ${p.plate}`.toLowerCase().includes(q)) return false;
         return true;
      });
   }, [rangePackages, statusFilter, planFilter, locationFilter, providerFilter, search]);

   // ── Sales-focused KPIs: every number here is tied to AED, not just a count ──
   const kpis = useMemo(() => {
      const activePkgs = rangePackages.filter((p) => p.status === 'Active');
      const expiringPkgs = rangePackages.filter((p) => p.status === 'Expiring Soon');
      const livePkgs = [...activePkgs, ...expiringPkgs];
      const grossSales = livePkgs.reduce((sum, p) => sum + p.money.customerCharged, 0);
      const netRevenue = livePkgs.reduce((sum, p) => sum + p.money.washtaNet, 0);
      const avgPackageValue = livePkgs.length ? grossSales / livePkgs.length : 0;
      const expiringSoonValue = expiringPkgs.reduce((sum, p) => sum + p.money.customerCharged, 0);

      const expiredPkgs = rangePackages.filter((p) => p.status === 'Expired');
      const renewedPkgs = expiredPkgs.filter((p) => p.renewed);
      const churnedPkgs = expiredPkgs.filter((p) => !p.renewed);
      const renewalRate = expiredPkgs.length ? Math.round((renewedPkgs.length / expiredPkgs.length) * 100) : 0;

      return {
         active: activePkgs.length,
         grossSales,
         netRevenue,
         avgPackageValue,
         expiringSoon: expiringPkgs.length,
         expiringSoonValue,
         renewalRate,
         renewedCount: renewedPkgs.length,
         renewedRevenue: renewedPkgs.reduce((sum, p) => sum + p.money.customerCharged, 0),
         churnedCount: churnedPkgs.length,
         churnedRevenue: churnedPkgs.reduce((sum, p) => sum + p.money.customerCharged, 0),
      };
   }, [rangePackages]);

   // ── Lifetime totals — feeds the revenue-formula box ──
   const formulaTotals = useMemo(() => rangePackages.reduce(addMoney, emptyMoney()), [rangePackages]);

   // ── Full per-provider financial breakdown — which provider is actually selling, not just fulfilling ──
   const providerBreakdown = useMemo(() => {
      const map = {};
      rangePackages.forEach((p) => {
         map[p.provider] = addMoney(map[p.provider] || emptyMoney(), p);
      });
      return Object.entries(map)
         .sort((a, b) => b[1].customerCharged - a[1].customerCharged)
         .map(([name, v]) => ({ name, ...v }));
   }, [rangePackages]);
   const maxProviderGross = Math.max(1, ...providerBreakdown.map((p) => p.customerCharged));

   // ── Full per-plan financial breakdown — which tier actually brings in the money, not just signups ──
   const planBreakdown = useMemo(() => {
      const map = {};
      rangePackages.forEach((p) => {
         map[p.plan] = addMoney(map[p.plan] || emptyMoney(), p);
      });
      return Object.entries(map)
         .sort((a, b) => b[1].customerCharged - a[1].customerCharged)
         .map(([name, v]) => ({ name, ...v }));
   }, [rangePackages]);

   // ── Full per-location breakdown (collapsible table) + top 5 (right-panel cards) ──
   const locationsBreakdown = useMemo(() => {
      const map = {};
      rangePackages.forEach((p) => {
         if (!map[p.location]) map[p.location] = { count: 0, revenue: 0 };
         map[p.location].count += 1;
         map[p.location].revenue += p.money.customerCharged;
      });
      return Object.entries(map)
         .sort((a, b) => b[1].revenue - a[1].revenue)
         .map(([label, v]) => ({ label, ...v }));
   }, [rangePackages]);
   const topLocationsByRevenue = locationsBreakdown.slice(0, 5);

   const notifyExpiring = () => {
      alert(`Notify ${kpis.expiringSoon} customers with packages expiring within 7 days — ${fmtAED(kpis.expiringSoonValue)} in renewal value at stake.`);
   };

   const exportPackagesCSV = () => {
      const header = ['Package ID', 'Customer', 'Email', 'Plan', 'Vehicle', 'Plate', 'Location', 'Provider', 'Washes Used', 'Washes Total', 'Purchased', 'Expires', 'Amount Paid', 'Net Revenue', 'Status'];
      const lines = filtered.map((p) => [
         p.id, p.customer, p.email, p.plan, p.vehicle, p.plate, p.location, p.provider,
         p.washesUsed, p.planTier, fmtDate(p.purchasedOn), fmtDate(p.expiresOn), p.money.customerCharged, p.money.washtaNet, p.status,
      ]);
      const csv = [header, ...lines].map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(',')).join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'washta-crm-packages.csv';
      a.click();
      URL.revokeObjectURL(url);
   };

   return (
      <div style={{ display: 'flex', width: '100%', minWidth: 0 }}>
         <div className="ws-view-block" style={{ flex: 1, minWidth: 0 }}>
            <div className="ws-package-page-head">
               <div className="ws-package-heading">
                  <div className="ws-view-title" style={{ marginBottom: 0 }}>Package Sales & Subscriptions</div>
                  <div className="ws-view-sub">
                     Sales performance for every monthly wash package sold — revenue, renewals, and churn by plan, provider, and location.
                  </div>
               </div>
               <div className="ws-package-date-filters" aria-label="Filter packages by purchase date">
                  {['Today', 'This Week', 'This Month', 'All time'].map((r) => (
                     <button key={r} className={`ws-date-pill ${range === r ? 'ws-on' : ''}`} onClick={() => setRange(r)}>{r}</button>
                  ))}
               </div>
            </div>

            <div className="ws-kpi-strip ws-kpi-6">
               <div className="ws-kpi-card ws-hl">
                  <div className="ws-kpi-label">Active Packages</div>
                  <div className="ws-kpi-val ws-purple">{kpis.active}</div>
                  <div className="ws-kpi-delta">Currently in use</div>
               </div>
               <div className="ws-kpi-card ws-gr">
                  <div className="ws-kpi-label">Gross Sales (Live)</div>
                  <div className="ws-kpi-val ws-green">{fmtAED(kpis.grossSales)}</div>
                  <div className="ws-kpi-delta ws-up">Active + Expiring Soon</div>
               </div>
               <div className="ws-kpi-card ws-dk">
                  <div className="ws-kpi-label">Washta Net Revenue</div>
                  <div className="ws-kpi-val ws-white">{fmtAED(kpis.netRevenue)}</div>
                  <div className="ws-kpi-delta ws-lv">Commission + fee − Stripe cost</div>
               </div>
               <div className="ws-kpi-card">
                  <div className="ws-kpi-label">Avg. Package Value</div>
                  <div className="ws-kpi-val">{fmtAED(kpis.avgPackageValue)}</div>
                  <div className="ws-kpi-delta">Per live subscription</div>
               </div>
               <div className="ws-kpi-card ws-am">
                  <div className="ws-kpi-label">Expiring Soon</div>
                  <div className="ws-kpi-val ws-amber">{kpis.expiringSoon}</div>
                  <div className="ws-kpi-delta">{fmtAED(kpis.expiringSoonValue)} renewal opportunity</div>
               </div>
               <div className="ws-kpi-card ws-rd">
                  <div className="ws-kpi-label">Renewal Rate</div>
                  <div className="ws-kpi-val ws-red">{kpis.renewalRate}%</div>
                  <div className="ws-kpi-delta ws-dn">{fmtAED(kpis.churnedRevenue)} lost to churn</div>
               </div>
            </div>

            {/* REVENUE FORMULA BOX — how one package sale turns into Washta's net revenue */}
            <div className="ws-formula-box">
               <div className="ws-formula-title">Revenue formula per package — totals across all {packages.length} packages ever sold</div>
               <div className="ws-f-row"><span className="ws-f-tag ws-ft-base">PACKAGE PRICE</span>What the provider charges before any fees<span className="ws-f-eq">{fmtAED(formulaTotals.packagePrice)}</span></div>
               <div className="ws-f-row"><span className="ws-f-tag ws-ft-fee">PLATFORM FEE</span>Flat AED 5 booking fee added to every package<span className="ws-f-eq">+ {fmtAED(formulaTotals.platformFee)}</span></div>
               <div className="ws-f-row"><span className="ws-f-tag ws-ft-gross">CHARGED TO CUSTOMER</span>Package price + platform fee — what hits the customer's card<span className="ws-f-eq">{fmtAED(formulaTotals.customerCharged)}</span></div>
               <div className="ws-f-row"><span className="ws-f-tag ws-ft-stripe">STRIPE FEE</span>Card processing cost on the charged amount<span className="ws-f-eq">− {fmtAED(formulaTotals.stripeFee)}</span></div>
               <div className="ws-f-row"><span className="ws-f-tag ws-ft-comm">COMMISSION</span>Washta's 8% cut of the package price<span className="ws-f-eq">+ {fmtAED(formulaTotals.commission)}</span></div>
               <div className="ws-f-row"><span className="ws-f-tag ws-ft-liab">PROVIDER PAYOUT</span>Package price − commission — what the provider keeps<span className="ws-f-eq">{fmtAED(formulaTotals.providerPayout)}</span></div>
               <div className="ws-f-row ws-f-row-total"><span className="ws-f-tag ws-ft-net">WASHTA NET</span>Commission + platform fee − Stripe fee — Washta's real take-home<span className="ws-f-eq ws-f-eq-net">{fmtAED(formulaTotals.washtaNet)}</span></div>
            </div>

            {/* PROVIDER COMPARISON */}
            <div className="ws-sec-head">
               <div className="ws-sec-title">Provider comparison · package sales</div>
               <button className="ws-btn-ex" onClick={exportPackagesCSV}>↓ Export CSV</button>
            </div>

            <div className="ws-chart-wrap">
               <div className="ws-chart-head">
                  <div className="ws-chart-title">Gross sales by provider</div>
                  <div className="ws-chart-legend">
                     <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <div className="ws-legend-dot" style={{ background: '#6B5FE4' }}></div>
                        <span className="ws-legend-label">Gross sales</span>
                     </div>
                     <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <div className="ws-legend-dot" style={{ background: '#d8d2ff' }}></div>
                        <span className="ws-legend-label">Washta net</span>
                     </div>
                  </div>
               </div>
               <div className="ws-chart-body">
                  {providerBreakdown.map((p) => (
                     <div className="ws-bar-row" key={p.name}>
                        <span className="ws-bar-label">{p.name}</span>
                        <div className="ws-bar-track">
                           <div className="ws-bar-fill" style={{ width: `${Math.max(2, (p.customerCharged / maxProviderGross) * 100)}%`, background: '#6B5FE4' }}></div>
                        </div>
                        <span className="ws-bar-val">{fmtAED(p.customerCharged)}</span>
                     </div>
                  ))}
               </div>
            </div>

            <div className="ws-table-scroll">
               <table className="ws-tbl" style={{ minWidth: 900 }}>
                  <thead>
                     <tr>
                        <th>Provider</th><th className="ws-r">Packages Sold</th><th className="ws-r">Gross Sales</th>
                        <th className="ws-r">Platform Fees</th><th className="ws-r">Stripe Fees</th>
                        <th className="ws-r">Commission Earned</th><th className="ws-r">Washta Net</th><th className="ws-r">Provider Payout</th>
                     </tr>
                  </thead>
                  <tbody>
                     {providerBreakdown.map((p) => (
                        <tr key={p.name}>
                           <td style={{ fontWeight: 700 }}>{p.name}</td>
                           <td className="ws-r">{p.count}</td>
                           <td className="ws-r">{fmtAED(p.customerCharged)}</td>
                           <td className="ws-r">{fmtAED(p.platformFee)}</td>
                           <td className="ws-r">{fmtAED(p.stripeFee)}</td>
                           <td className="ws-r">{fmtAED(p.commission)}</td>
                           <td className="ws-r" style={{ fontWeight: 700, color: '#4a3db8' }}>{fmtAED(p.washtaNet)}</td>
                           <td className="ws-r">{fmtAED(p.providerPayout)}</td>
                        </tr>
                     ))}
                  </tbody>
                  <tfoot>
                     <tr>
                        <td>Platform Total</td>
                        <td className="ws-r">{formulaTotals.count}</td>
                        <td className="ws-r">{fmtAED(formulaTotals.customerCharged)}</td>
                        <td className="ws-r">{fmtAED(formulaTotals.platformFee)}</td>
                        <td className="ws-r">{fmtAED(formulaTotals.stripeFee)}</td>
                        <td className="ws-r">{fmtAED(formulaTotals.commission)}</td>
                        <td className="ws-r">{fmtAED(formulaTotals.washtaNet)}</td>
                        <td className="ws-r">{fmtAED(formulaTotals.providerPayout)}</td>
                     </tr>
                  </tfoot>
               </table>
            </div>

            {/* TOP LOCATIONS BY REVENUE — collapsible */}
            <div style={{ marginTop: 4 }}>
               <div className={`ws-collapsible-head ${locationsOpen ? 'ws-open' : ''}`} onClick={() => setLocationsOpen((p) => !p)}>
                  <div className="ws-collapsible-title">📍 Top Locations by Revenue</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                     <span className="ws-collapsible-meta">{locationsBreakdown.length} locations</span>
                     <span style={{ fontSize: 12, color: '#aaa' }}>{locationsOpen ? '▲' : '▼'}</span>
                  </div>
               </div>
               {locationsOpen && (
                  <div className="ws-collapsible-body">
                     <table className="ws-promo-tbl">
                        <thead>
                           <tr><th>Rank</th><th>Location</th><th className="ws-r">Packages Sold</th><th className="ws-r">Revenue</th></tr>
                        </thead>
                        <tbody>
                           {locationsBreakdown.map((l, i) => (
                              <tr key={l.label}>
                                 <td><span className={`ws-rank-dot ${rankClass(i)}`}>{i + 1}</span></td>
                                 <td>{l.label}</td>
                                 <td className="ws-r">{l.count}</td>
                                 <td className="ws-r">{fmtAED(l.revenue)}</td>
                              </tr>
                           ))}
                        </tbody>
                     </table>
                  </div>
               )}
            </div>

            {/* PLAN TIER PERFORMANCE — collapsible */}
            <div style={{ marginTop: 16 }}>
               <div className={`ws-collapsible-head ${planOpen ? 'ws-open' : ''}`} onClick={() => setPlanOpen((p) => !p)}>
                  <div className="ws-collapsible-title">
                     📦 Plan Tier Performance
                     <span className="ws-count-badge">{planBreakdown.length} plans</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                     <span className="ws-collapsible-meta">Total: {fmtAED(formulaTotals.customerCharged)} across {formulaTotals.count} packages</span>
                     <span style={{ fontSize: 12, color: '#aaa' }}>{planOpen ? '▲' : '▼'}</span>
                  </div>
               </div>
               {planOpen && (
                  <div className="ws-collapsible-body">
                     <table className="ws-promo-tbl">
                        <thead>
                           <tr><th>Plan</th><th className="ws-r">Packages Sold</th><th className="ws-r">Gross Sales</th><th className="ws-r">Avg Value</th><th className="ws-r">Washta Net</th></tr>
                        </thead>
                        <tbody>
                           {planBreakdown.map((pl) => (
                              <tr key={pl.name}>
                                 <td><span className="ws-promo-code-badge">{pl.name}</span></td>
                                 <td className="ws-r">{pl.count}</td>
                                 <td className="ws-r">{fmtAED(pl.customerCharged)}</td>
                                 <td className="ws-r">{fmtAED(pl.count ? pl.customerCharged / pl.count : 0)}</td>
                                 <td className="ws-r" style={{ fontWeight: 700, color: '#4a3db8' }}>{fmtAED(pl.washtaNet)}</td>
                              </tr>
                           ))}
                        </tbody>
                     </table>
                  </div>
               )}
            </div>

            {/* PACKAGE LIST */}
            <div className="ws-sec-head" style={{ marginTop: 20 }}>
               <div className="ws-sec-title">All Packages</div>
            </div>

            <div className="ws-filter-row">
               <select className="ws-f-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                  {['All Statuses', ...packageStatuses].map((s) => <option key={s} value={s}>{s}</option>)}
               </select>
               <select className="ws-f-select" value={planFilter} onChange={(e) => setPlanFilter(e.target.value)}>
                  {['All Plans', ...packagePlans].map((p) => <option key={p} value={p}>{p}</option>)}
               </select>
               <select className="ws-f-select" value={locationFilter} onChange={(e) => setLocationFilter(e.target.value)}>
                  {['All Locations', ...packageLocations].map((l) => <option key={l} value={l}>{l}</option>)}
               </select>
               <select className="ws-f-select" value={providerFilter} onChange={(e) => setProviderFilter(e.target.value)}>
                  {['All Providers', ...packageProviders].map((p) => <option key={p} value={p}>{p}</option>)}
               </select>
               <input
                  className="ws-f-input ws-ml"
                  placeholder="Customer, email, plate…"
                  style={{ width: 170 }}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
               />
               <button className="ws-btn-ex" onClick={exportPackagesCSV}>↓ Export</button>
            </div>

            <table className="ws-tbl">
               <thead>
                  <tr>
                     <th>Customer</th>
                     <th>Plan</th>
                     <th>Vehicle</th>
                     <th>Location · Provider</th>
                     <th>Usage</th>
                     <th>Purchased</th>
                     <th>Expires</th>
                     <th className="ws-r">Customer Paid</th>
                     <th className="ws-r">Net Revenue</th>
                     <th>Status</th>
                  </tr>
               </thead>
               <tbody>
                  {filtered.length === 0 ? (
                     <tr>
                        <td colSpan={10} style={{ textAlign: 'center', color: '#aaa', padding: '18px 10px' }}>
                           No packages match the current filters.
                        </td>
                     </tr>
                  ) : filtered.map((p) => {
                     const daysLeft = daysUntil(p.expiresOn);
                     return (
                        <tr key={p.id} style={{ cursor: 'pointer', background: selectedPkg?.id === p.id ? '#f5f3ff' : undefined }} onClick={() => setSelectedPkg(p)}>
                           <td>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                 <div className="ws-avatar">{p.initials}</div>
                                 <div>
                                    <div style={{ fontWeight: 600, fontSize: 11 }}>{p.customer}</div>
                                    <div style={{ fontSize: 9, color: '#aaa' }}>{p.email}</div>
                                 </div>
                              </div>
                           </td>
                           <td>
                              <div style={{ fontSize: 11, fontWeight: 600, color: '#4a3db8' }}>{p.plan}</div>
                              <div style={{ fontSize: 9, color: '#888' }}>{p.frequency}</div>
                           </td>
                           <td>
                              <div style={{ fontSize: 11 }}>{p.vehicle}</div>
                              <div><span className="ws-plate">{p.plate}</span> <span style={{ fontSize: 9, color: '#888' }}>{p.carType}</span></div>
                           </td>
                           <td>
                              <div style={{ fontSize: 11 }}>{p.location}</div>
                              <div style={{ fontSize: 9, color: '#888' }}>{p.provider}</div>
                           </td>
                           <td><UsageBar used={p.washesUsed} total={p.planTier} /></td>
                           <td style={{ fontSize: 11 }}>{fmtDate(p.purchasedOn)}</td>
                           <td style={{ fontSize: 11, color: p.status === 'Expiring Soon' ? '#92400E' : p.status === 'Expired' ? '#991b1b' : '#111' }}>
                              {fmtDate(p.expiresOn)}
                              {p.status !== 'Expired' && p.status !== 'Cancelled' && daysLeft != null && (
                                 <div style={{ fontSize: 9, color: '#aaa' }}>{daysLeft}d left</div>
                              )}
                           </td>
                           <td className="ws-r" style={{ fontWeight: 700 }}>{fmtAED(p.money.customerCharged)}</td>
                           <td className="ws-r" style={{ fontWeight: 700, color: '#1a6641' }}>{fmtAED(p.money.washtaNet)}</td>
                           <td><StatusPill status={p.status} /></td>
                        </tr>
                     );
                  })}
               </tbody>
            </table>
            <div style={{ fontSize: 11, color: '#888', marginTop: 10, paddingTop: 10, borderTop: '1px solid #f0f0f0' }}>
               Showing {filtered.length} of {packages.length} package subscriptions{usingMockData ? ' (mock data)' : ''}.
            </div>
         </div>

         {selectedPkg ? (
            <PackageDrawer pkg={selectedPkg} onClose={() => setSelectedPkg(null)} />
         ) : (
            <div className="ws-right-panel">
               <div className="ws-rp-title">Top Locations</div>
               {topLocationsByRevenue.map((l, i) => (
                  <div className="ws-rp-item" key={l.label}>
                     <div className="ws-rp-name">
                        <span className={`ws-rank-dot ${rankClass(i)}`}>{i + 1}</span>{l.label}
                     </div>
                     <div className="ws-rp-stats">
                        <div><div className="ws-rp-stat-val">{l.count}</div><div className="ws-rp-stat-label">packages</div></div>
                        <div><div className="ws-rp-stat-val">{fmtAED(l.revenue)}</div><div className="ws-rp-stat-label">revenue</div></div>
                     </div>
                  </div>
               ))}

               <div className="ws-divider"></div>
               <div className="ws-rp-title">Renewal Health</div>
               <div style={{ display: 'flex', gap: 8, marginBottom: 4 }}>
                  <div style={{ flex: 1, background: '#e0f2ea', borderRadius: 8, padding: '8px 10px' }}>
                     <div style={{ fontSize: 9, fontWeight: 700, color: '#1a6641', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Renewed</div>
                     <div style={{ fontSize: 14, fontWeight: 700, color: '#1a6641', marginTop: 2 }}>{kpis.renewedCount}</div>
                     <div style={{ fontSize: 9, color: '#1a6641', marginTop: 1 }}>{fmtAED(kpis.renewedRevenue)} retained</div>
                  </div>
                  <div style={{ flex: 1, background: '#fee2e2', borderRadius: 8, padding: '8px 10px' }}>
                     <div style={{ fontSize: 9, fontWeight: 700, color: '#991b1b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Churned</div>
                     <div style={{ fontSize: 14, fontWeight: 700, color: '#991b1b', marginTop: 2 }}>{kpis.churnedCount}</div>
                     <div style={{ fontSize: 9, color: '#991b1b', marginTop: 1 }}>{fmtAED(kpis.churnedRevenue)} lost</div>
                  </div>
               </div>

               <div className="ws-divider"></div>
               <div className="ws-rp-title">Quick Actions</div>
               <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                  <button className="ws-btn-sm ws-btn-gh" style={{ textAlign: 'left', fontSize: 10 }} onClick={notifyExpiring}>
                     ⏱ Notify expiring-soon customers
                  </button>
                  <button className="ws-btn-sm ws-btn-gh" style={{ textAlign: 'left', fontSize: 10 }} onClick={exportPackagesCSV}>
                     📤 Export package list CSV
                  </button>
               </div>
            </div>
         )}
         <style>{`
            .ws-package-drawer { width: 320px; flex: 0 0 320px; align-self: flex-start; position: sticky; top: 12px; max-height: calc(100vh - 24px); overflow-y: auto; background: #fff; border-left: 1px solid #eee; }
            .ws-package-drawer-head { background: #f7f7f9; border-bottom: 1px solid #eee; padding: 10px 14px; display: flex; align-items: center; justify-content: space-between; position: sticky; top: 0; z-index: 1; }
            .ws-package-page-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; margin-bottom: 16px; }
            .ws-package-heading { min-width: 0; }
            .ws-package-heading .ws-view-sub { margin-bottom: 0; }
            .ws-package-date-filters { display: flex; flex: 0 0 auto; align-items: center; justify-content: flex-end; gap: 6px; white-space: nowrap; }
            .ws-package-date-filters .ws-date-pill { white-space: nowrap; }
            @media (max-width: 760px) {
               .ws-package-drawer { position: fixed; z-index: 1000; top: 10px; right: 10px; bottom: 10px; width: min(380px, calc(100vw - 20px)); max-height: none; box-shadow: 0 8px 32px rgba(0,0,0,.24); border: 1px solid #eee; border-radius: 12px; }
               .ws-package-page-head { flex-direction: column; align-items: stretch; gap: 8px; }
               .ws-package-date-filters { justify-content: flex-start; flex-wrap: nowrap; overflow-x: auto; padding: 2px 0 6px; }
               .ws-package-date-filters .ws-date-pill { flex: 0 0 auto; }
            }
         `}</style>
      </div>
   );
};

export default CrmPackages;
